// PostDetailJsonLd.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import serialize from 'serialize-javascript';
import { withTranslation } from 'react-i18next';

import {
  getCoporation,
  getPerson,
  getInteractionCounter,
  getPostVideoObject,
  getPostPreviewVideoObject,
} from '../resource/SchemaObject.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

export const PostDetailJsonLd = ({
  messageId,
  title,
  description,
  senderId,
  senderName,
  jpg1x,
  m3u8Url,
  m3u8PreviewUrl,
  postedAtUnix,
  isFree,
  viewCount,
  likeCount,
  unlockCount,
  relatedMessageIds,
  hashtagContentList,
  t,
}) => {
  const postVideoObject = getPostVideoObject({
    id: messageId,
    username: senderName,
    title,
    description: description || title,
    postedAtUnix,
    isFree,
    author: {
      '@id': `https://swag.live/user/${senderId}#person`,
    },
    interactionStatistic: [
      { type: 'ViewAction', count: viewCount },
      { type: 'LikeAction', count: likeCount },
      { type: 'BuyAction', count: unlockCount },
    ].map(getInteractionCounter),
    encodings: [{ '@id': `https://swag.live/post/${messageId}#video-dash` }],
    associatedMedia: {
      '@id': `https://swag.live/post/${messageId}#preview`,
    },
    thumbnailUrl: jpg1x,
    contentUrl: m3u8Url,
    hashtags: hashtagContentList.map(content => `#${content}`),
  });
  const headline = t('seo_cp_video_title', {
    flix_title: postVideoObject.name,
    ns: TranslationNamespace.SEO,
  });
  const articleBody = t('seo_cp_video_description', {
    cp_name: senderName,
    flix_title: postVideoObject.name,
    flix_description: postVideoObject.description,
    ns: TranslationNamespace.SEO,
  });
  const jsonLd = serialize(
    {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BlogPosting',
          headline,
          articleBody,
          sharedContent: {
            '@id': postVideoObject['@id'],
          },
          image: [postVideoObject.thumbnailUrl],
          datePublished: postVideoObject.uploadDate,
          author: {
            '@id': `https://swag.live/user/${senderId}#person`,
          },
          publisher: getCoporation(),
          relatedLink: relatedMessageIds.map(
            messageId => `https://swag.live/post/${messageId}`
          ),
        },
        postVideoObject,
        getPerson({ id: senderId, username: senderName }),
        getPostPreviewVideoObject({
          id: messageId,
          title,
          description: description || title,
          postedAtUnix,
          author: {
            '@id': `https://swag.live/user/${senderId}#person`,
          },
          thumbnailUrl: jpg1x,
          contentUrl: m3u8PreviewUrl,
        }),
        ...(hashtagContentList
          ? hashtagContentList
              .map(hashtagContent => `#${hashtagContent}`)
              .map(hashtag => ({
                '@type': 'VideoGallery',
                '@id': `https://swag.live/hashtag/${hashtag}/flix#webpage`,
                url: `https://swag.live/hashtag/${hashtag}/flix`,
                name: hashtag,
                breadcrumb: {
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      item: {
                        '@id': 'https://swag.live',
                        '@type': 'WebPage',
                        name: t('seo-home-title', {
                          ns: TranslationNamespace.SEO,
                        }),
                      },
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      item: {
                        '@id': `https://swag.live/hashtag/${hashtag}/flix#feed`,
                        '@type': 'WebPage',
                        name: `${hashtag} ${t('seo_flix_hashtag_title', {
                          ns: TranslationNamespace.SEO,
                        })} - ${t('seo_video_title', {
                          ns: TranslationNamespace.SEO,
                        })}`,
                      },
                    },
                  ],
                },
                mainEntity: {
                  '@id': `https://swag.live/hashtag/${hashtag}/flix#feed`,
                },
              }))
          : []),
      ],
    },
    { isJSON: true }
  );
  return (
    <Helmet>
      <script type="application/ld+json" helmetkey="jsonld">
        {jsonLd}
      </script>
    </Helmet>
  );
};

PostDetailJsonLd.propTypes = {
  messageId: PropTypes.string,
  senderId: PropTypes.string,
  senderName: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  jpg1x: PropTypes.string,
  m3u8Url: PropTypes.string,
  m3u8PreviewUrl: PropTypes.string,
  postedAtUnix: PropTypes.number,
  isFree: PropTypes.bool,
  viewCount: PropTypes.number,
  likeCount: PropTypes.number,
  unlockCount: PropTypes.number,
  relatedMessageIds: PropTypes.array,
  hashtagContentList: PropTypes.array,
  t: PropTypes.func.isRequired,
};

PostDetailJsonLd.defaultProps = {
  messageId: '',
  senderId: '',
  senderName: '',
  title: '',
  description: '',
  jpg1x: '',
  m3u8Url: '',
  m3u8PreviewUrl: '',
  postedAtUnix: 0,
  isFree: false,
  viewCount: 0,
  likeCount: 0,
  unlockCount: 0,
  relatedMessageIds: [],
  hashtagContentList: [],
};

export default withTranslation()(PostDetailJsonLd);
